import { Reference, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import ActionIconButton from "@components/buttons/ActionIconButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import ClientAvatar from "@components/ClientAvatar";
import ExpertAvailabilityPickerForExperts from "@components/expertSection/schedule/ExpertAvailabilityPickerForExperts";
import styles from "@components/expertSection/schedule/ScheduleSession.module.css";
import BaseErrors from "@components/fields/BaseErrors";
import Checkbox from "@components/fields/Checkbox";
import CheckboxGroup from "@components/fields/CheckboxGroup";
import DataSelect from "@components/fields/DataSelect";
import Input from "@components/fields/Input";
import PhoneInput from "@components/fields/PhoneInput";
import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import RichTextArea from "@components/fields/RichTextArea";
import NativeRadioButtonGroup, {
  OptionType as NativeRadioButtonGroupOption
} from "@components/fieldsNative/RadioButtonGroup";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import Tag from "@components/labels/Tag";
import ColumnGrid from "@components/layout/ColumnGrid";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import {
  ConsultAvailabilityFragment as ConsultAvailabilityType,
  ConsultCreateDocument,
  ConsultCreateMutation,
  ConsultCreateMutationVariables,
  ConsultDurationEnum,
  ConsultFragment,
  ConsultFragmentDoc,
  ContactMethodEnum,
  ExpertRoleRoleEnum,
  GetJourneyWithExpertAndConsultsQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { contactMethodOptions, durationOptions } from "@utils/optionUtils";

type Props = {
  journey: GetJourneyWithExpertAndConsultsQuery["journey"];
};

export type FormData = {
  contactMethod: ContactMethodEnum | null;
  email: string | null;
  inviteEmails: string[];
  invitedClientIds: string[];
  languageCode: string | null;
  languageRequest: boolean;
  notes: string | null;
  phoneNumber?: string | null | undefined;
};

const ExpertScheduleSession = ({ journey }: Props) => {
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [confirmedConsult, setConfirmedConsult] = useState<ConsultFragment | null>();
  const [duration, setDuration] = useState<ConsultDurationEnum | null>(null);

  const intl = useIntl();

  const currentClientRole = journey.clientRoles.find(role => role.client.id === journey.owner.id);
  const initialPhoneNumber = journey.owner.consultPhoneNumber ?? journey.owner.verificationPhoneNumber;

  const defaultValues: FormData = {
    contactMethod: null,
    email: null,
    inviteEmails: [],
    invitedClientIds: currentClientRole ? [currentClientRole.client.id] : [],
    languageCode: null,
    languageRequest: false,
    notes: "",
    phoneNumber: initialPhoneNumber?.number ?? null
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      contactMethod: yup
        .string()
        .oneOf(Object.values(ContactMethodEnum))
        .required(intl.formatMessage({ id: "errors.consult.contactMethod.blank" })),
      email: yup
        .string()
        .defined()
        .email(intl.formatMessage({ id: "errors.inviteAccountMembers.emailFormat" }))
        .nullable(),
      inviteEmails: yup.array().defined().of(yup.string().defined()),
      invitedClientIds: yup.array().defined().of(yup.string().defined()),
      languageCode: yup.string().nullable().defined(),
      languageRequest: yup.boolean().defined(),
      notes: yup.string().nullable().defined(),
      phoneNumber: yup
        .string()
        .test(
          "is-valid-phone",
          intl.formatMessage({ id: "errors.consult.phoneNumber.blank" }),
          value => !value || isValidPhoneNumber(value)
        )
        .when("contactMethod", {
          is: ContactMethodEnum.PHONE,
          otherwise: schema => schema.nullable(),
          then: schema => schema.required(intl.formatMessage({ id: "errors.consult.phoneNumber.blank" }))
        })
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const contactMethod = form.watch("contactMethod");
  const email = form.watch("email");
  const inviteEmails = form.watch("inviteEmails");
  const invitedClientIds = form.watch("invitedClientIds");
  const languageCode = form.watch("languageCode");
  const languageRequest = form.watch("languageRequest");
  const notes = form.watch("notes");
  const phoneNumber = form.watch("phoneNumber");

  const [createConsult, { client }] = useMutation<ConsultCreateMutation, ConsultCreateMutationVariables>(
    ConsultCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.consultCreate?.consult) {
          if (
            !journey.expertRoles?.some(
              role =>
                role.expert?.id === data.consultCreate?.consult?.expert.id &&
                [ExpertRoleRoleEnum.PRIMARY, ExpertRoleRoleEnum.TEAM_MEMBER].includes(role.role)
            )
          ) {
            client.resetStore();
          }
          store.modify({
            fields: {
              consults(cachedConsultRefs: readonly Reference[]) {
                const newConsultRef = store.writeFragment({
                  data: data.consultCreate?.consult,
                  fragment: ConsultFragmentDoc,
                  fragmentName: "Consult"
                });
                if (newConsultRef && data?.consultCreate?.consult) {
                  return [...cachedConsultRefs, newConsultRef];
                }
                return [...cachedConsultRefs];
              }
            },
            id: store.identify({ ...journey })
          });
        }
      }
    }
  );

  const [availabilitySelected, setAvailabilitySelected] = useState<ConsultAvailabilityType | null>(null);

  const onSubmit = useCallback(async () => {
    if (contactMethod && availabilitySelected && journey.primaryExpert) {
      const emailInvitessForConsult = inviteEmails.filter(
        email => !journey.careTeamInvites?.some(invite => invite.email === email)
      );

      return createConsult({
        variables: {
          availability: {
            duration: availabilitySelected.duration,
            expertId: availabilitySelected.expert.id,
            startTime: availabilitySelected.startTime
          },
          contactMethod,
          formattedNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : null,
          invitedClientIds: invitedClientIds ?? journey.clientRoles.map(role => role.client.id),
          invitedEmails: emailInvitessForConsult,
          journeyId: journey.id,
          languageCode,
          notes,
          number: phoneNumber,
          smsReminders: false
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.consultCreate,
          intl,
          onSuccess: () => {
            if (response.data?.consultCreate?.consult) {
              setConfirmedConsult(response.data?.consultCreate.consult);
              form.reset();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.consult"
        });
      });
    }
  }, [
    availabilitySelected,
    contactMethod,
    createConsult,
    form,
    intl,
    inviteEmails,
    invitedClientIds,
    journey.careTeamInvites,
    journey.clientRoles,
    journey.id,
    journey.primaryExpert,
    languageCode,
    notes,
    phoneNumber
  ]);

  const inviteeOptions = useMemo(() => {
    const otherClientRoles = journey.clientRoles.filter(role => role.client.id);

    return otherClientRoles.map(option => ({
      label: option.client.fullName,
      value: option.client.id
    }));
  }, [journey.clientRoles]);

  const handleDurationChange = useCallback((option: NativeRadioButtonGroupOption) => {
    setDuration(option.value as ConsultDurationEnum);
  }, []);

  return (
    <div className={styles.expertSchedule}>
      {confirmedConsult ? (
        <div className="align-center">
          <div className={styles.spacingBottomLarge}>
            <Heading2>
              <FormattedMessage id="pages.consults.booked.scheduled" />
            </Heading2>
          </div>
          <PrimaryButton onClick={() => setConfirmedConsult(null)} textKey="pages.consults.booked.bookAnother" />
        </div>
      ) : (
        <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <ColumnGrid columns={2}>
            <div>
              <NativeRadioButtonGroup
                currentSelection={duration as string}
                fullWidth
                onChange={handleDurationChange}
                options={durationOptions(intl)}
              />
              <div className={styles.label}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="forms.consult.availabilityDateLabel" />
                </BodyCopy>
              </div>
              <ExpertAvailabilityPickerForExperts
                availabilitySelected={availabilitySelected}
                duration={duration}
                journey={journey}
                setAvailabilitySelected={setAvailabilitySelected}
              />
              <div className={styles.label}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="forms.consult.contactMethodLabel" />
                </BodyCopy>
              </div>
              <RadioButtonGroup<FormData>
                control={form.control}
                error={form.formState.errors.contactMethod}
                fullWidth
                name="contactMethod"
                options={contactMethodOptions(intl)}
              />
              <FormCondition condition={contactMethod === ContactMethodEnum.PHONE} fieldName="phoneNumber" form={form}>
                <div className={styles.label}>
                  <BodyCopy color="gray-500">
                    <FormattedMessage id="forms.consult.expertPhoneNumberLabel" />
                  </BodyCopy>
                </div>
                <PhoneInput<FormData>
                  autofocus
                  control={form.control}
                  error={form.formState.errors.phoneNumber}
                  name="phoneNumber"
                />
              </FormCondition>
            </div>
            <div>
              <div className={styles.label}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="forms.consult.inviteOthers" />
                </BodyCopy>
              </div>
              <CheckboxGroup<FormData>
                control={form.control}
                errors={form.formState.errors.invitedClientIds}
                label=""
                name="invitedClientIds"
                options={inviteeOptions}
                required
              />
              <div className={styles.relativeContainer}>
                <Input<FormData>
                  actions={[
                    {
                      ariaLabelKey: "accessibility.ariaLabels.add",
                      disabled: !!form.formState.errors.email || !email,
                      icon: "add",
                      onClick: () => {
                        const newInviteEmails = [...inviteEmails, email ?? ""];
                        form.setValue(
                          "inviteEmails",
                          newInviteEmails.filter((v, i) => newInviteEmails.indexOf(v) === i)
                        );

                        form.setValue("email", "", { shouldTouch: true, shouldValidate: true });
                      }
                    }
                  ]}
                  data-test="email-input"
                  error={form.formState.errors.email}
                  noSpacing
                  placeholder="email@withgrayce.com"
                  register={form.register("email")}
                  type="email"
                />
              </div>
              <div>
                {inviteEmails.map(email => (
                  <div className={styles.spaceBetween} key={email}>
                    <div className={styles.expertInviteEmail}>
                      <ClientAvatar clientId={null} firstName={email} journeyId={journey.id} />
                      <Tag color="primary-50" text={email} textColor="primary" />
                    </div>
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      icon="trash"
                      onClick={() =>
                        form.setValue(
                          "inviteEmails",
                          inviteEmails.filter(value => value !== email)
                        )
                      }
                      skipSpacing
                    />
                  </div>
                ))}
              </div>
              <div>
                <Checkbox<FormData>
                  control={form.control}
                  error={form.formState.errors.languageRequest}
                  name="languageRequest"
                >
                  <BodyCopy color="gray-500">
                    <FormattedMessage id="pages.client.consults.requestLanguageSupport" />
                  </BodyCopy>
                </Checkbox>
              </div>
              <div className={languageRequest ? styles.spacingBottom : "ion-hide"}>
                <DataSelect<FormData>
                  control={form.control}
                  error={form.formState.errors.languageCode}
                  hasNoneOption
                  label=""
                  name="languageCode"
                  type="languages"
                />
              </div>
              <div className={styles.label}>
                <BodyCopy color="gray-500">
                  <FormattedMessage id="pages.consults.confirm.discuss" />
                </BodyCopy>
              </div>
              <RichTextArea<FormData>
                control={form.control}
                error={form.formState.errors.notes}
                journeyId={journey.id}
                name="notes"
                placeholder={intl.formatMessage({ id: "dictionary.typeHere" })}
                required
                size="smallish"
                toolbarHidden
              />
            </div>
          </ColumnGrid>
          <div className={styles.expertSubmitBox}>
            <PrimaryButton
              data-test="submit-button"
              disabled={!form?.formState.isValid || form?.formState.isSubmitting || !availabilitySelected}
              textKey="pages.consults.scheduleSession"
              type="submit"
            />
            <div className="align-center">
              <BaseErrors errors={baseErrors} />
            </div>
          </div>
        </Form>
      )}
    </div>
  );
};

export default ExpertScheduleSession;
